import React, { useState, useCallback, useMemo } from "react";
import { Spin } from "antd";
import SingleQuantity from "../quantities/single-quantity";
import { FormattedMessage } from "react-intl";
import useTicketOnePrices from "../../hooks/use-ticketone-prices";
import useTicketOneSalesTypes from "../../hooks/use-ticketone-salestypes";
import { IQuantity } from "../../interfaces/local-interfaces";
import { ITOPrice, ITOSalesType } from "../../interfaces/ticketone-interfaces";
import useLocale from "../../hooks/use-locale";

interface QuantityPanelProps {
  onChange: (quantities: IQuantity[]) => void;
  totalQty?: number;
  maxQuantity: number;
  total: number;
  eventNo: string;
  seatGroup: string | undefined;
}

const QuantityPanel: React.FC<QuantityPanelProps> = ({
  onChange,
  totalQty,
  maxQuantity,
  total,
  eventNo,
  seatGroup,
}) => {
  const [loadingPrices, prices] = useTicketOnePrices(eventNo);
  const [loadingSalesTypes, salesTypes] = useTicketOneSalesTypes(eventNo);
  const [quantities, setQuantities] = useState<IQuantity[]>([]);
  const [, translations] = useLocale();

  const availableSalesTypes = useMemo<ITOSalesType[]>(() => {
    if (!salesTypes) return [];
    const unique = new Map<string, ITOSalesType>();
    salesTypes
      .filter((salesType) => salesType.status !== "P")
      .forEach((salesType) => {
        if (!unique.has(salesType.internalKey)) {
          unique.set(salesType.internalKey, salesType);
        }
      });
    return Array.from(unique.values());
  }, [salesTypes]);

  const findPriceForSalesType = useCallback(
    (salesTypeKey: string): ITOPrice | undefined => {
      if (!prices) return undefined;
      const matches = prices.filter(
        (price) => price.salesType === salesTypeKey
      );
      if (!matches.length) return undefined;

      if (seatGroup) {
        const bySeatGroup = matches.find(
          (price) => price.seatGroup === seatGroup
        );
        if (bySeatGroup) return bySeatGroup;

        const bySeatGroupDescription = matches.find(
          (price) => price.seatGroupDescription === seatGroup
        );
        if (bySeatGroupDescription) return bySeatGroupDescription;
      }

      return matches[0];
    },
    [prices, seatGroup]
  );

  const onChangeQuantity = useCallback(
    (value: number, salesType: string, text: string) => {
      setQuantities((prevData) => {
        const qties: IQuantity[] = prevData.filter(
          (q) => q.salesType !== salesType
        );
        if (value > 0) {
          qties.push({
            salesType: salesType,
            value: value,
            text: text,
          });
        }
        onChange(qties);
        return qties;
      });
    },
    [setQuantities, onChange]
  );

  return (
      <Spin spinning={loadingSalesTypes || loadingPrices} delay={500}>
          <span>
              {!!totalQty && totalQty > 0 ? (
                  <span
                      dangerouslySetInnerHTML={{
                          __html: `${translations["third.tickets"]} <strong>${totalQty}</strong> 
                          (${translations["third.tickets_purchase"]} <strong>${maxQuantity}</strong>)`,
                      }}
                  />
              ) : (
                  <span
                      dangerouslySetInnerHTML={{
                          __html: `${translations["third.tickets_purchase"]} <strong>${maxQuantity}</strong>`,
                      }}
                  />
              )}
          </span>
          {false && (
              <p>
                  <FormattedMessage id="third.qty" />
              </p>
          )}
          <div className="adt_quantities">
              {availableSalesTypes.map((salesType) => {
                  const priceElement = findPriceForSalesType(
                    salesType.internalKey
                  );

                  if (!priceElement) return null;

                  return (
                    <SingleQuantity
                      key={salesType.internalKey}
                      salesType={salesType.internalKey}
                      price={priceElement.price}
                      text={salesType.text}
                      seatGroup={seatGroup}
                      freeQuantity={maxQuantity - total}
                      onChangeQuantity={onChangeQuantity}
                    />
                  );
              })}
          </div>
      </Spin>
  );
};
export default QuantityPanel;
